import React from 'react';
import { OverlayTrigger, Tooltip } from '@/components/ui';
import type { HiredEquipmentEntryMethod } from '../types';

export interface EntryMethodOption {
  method: HiredEquipmentEntryMethod;
  title: string;
  description: string;
}

export const ENTRY_METHOD_OPTIONS: EntryMethodOption[] = [
  {
    method: 'purchase_order',
    title: 'By Purchase Order',
    description:
      "Vendor and PO are fixed for the whole submission. Pre-populate the PO's resources, then set a task per line.",
  },
  {
    method: 'subcontractor',
    title: 'By Subcontractor',
    description: 'One vendor for the whole submission. Add lines across any of their POs and tasks.',
  },
  {
    method: 'task',
    title: 'By Task',
    description:
      'One task for the whole submission. Add lines for any vendor / PO that worked it — rolls up to the one task.',
  },
];

export interface EntryMethodCardProps {
  option: EntryMethodOption;
  selected: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onSelect: (method: HiredEquipmentEntryMethod) => void;
}

const EntryMethodCard: React.FC<EntryMethodCardProps> = ({
  option,
  selected,
  disabled = false,
  disabledReason,
  onSelect,
}) => {
  const selectedCls = selected
    ? 'tw-border-primary tw-bg-primary/10 tw-shadow-sm'
    : 'tw-border-line-input tw-bg-surface hover:tw-border-line-focus';
  const disabledCls = disabled ? 'tw-cursor-not-allowed tw-opacity-60 hover:tw-border-line-input' : 'tw-cursor-pointer';

  const card = (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onSelect(option.method);
      }}
      className={`tw-flex tw-h-full tw-w-full tw-flex-col tw-rounded-lg tw-border-2 tw-p-4 tw-text-left tw-transition ${selectedCls} ${disabledCls} focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-line-focus`}
    >
      <span className="tw-font-display tw-text-base tw-font-semibold tw-text-content">{option.title}</span>
      <span className="tw-mt-2 tw-text-sm tw-leading-snug tw-text-content-muted">{option.description}</span>
    </button>
  );

  if (disabled && disabledReason) {
    return (
      <OverlayTrigger placement="top" overlay={<Tooltip id={`he-method-${option.method}-disabled`}>{disabledReason}</Tooltip>}>
        <div className="tw-h-full">{card}</div>
      </OverlayTrigger>
    );
  }

  return card;
};

export default EntryMethodCard;
